import React, { useEffect, useMemo } from 'react';
import style from './UpgradePage.module.css';
import UpgradeCard from '../../components/UpgradeCard/UpgradeCard';
import SecondaryButton from '../../components/SecondaryButton/SecondaryButton';
import SEO from '../../components/SEO/SEO';
import { useAuthUser } from '../../hooks/useAuthUser';
import { useLocation, useNavigate } from 'react-router-dom';

const UpgradePage = () => {
	const { user } = useAuthUser();
	const navigate = useNavigate();
	const location = useLocation();
	const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
	const onboardingRoutineId = queryParams.get('routineId') || '';
	const onboardingSource = queryParams.get('source') === 'onboarding';
	const checkoutRedirectUrl = onboardingRoutineId
		? `${window.location.origin}/onboarding/payment-success?routineId=${encodeURIComponent(onboardingRoutineId)}`
		: '';

	const buildCheckoutPayload = useMemo(() => (variantId) => {
		const payload = {
			data: {
				type: 'checkouts',
				relationships: {
					store: {
						data: {
							type: 'stores',
							id: '86022'
						}
					},
					variant: {
						data: {
							type: 'variants',
							id: variantId
						}
					}
				}
			}
		};

		const customData = onboardingRoutineId && user?.id
			? {
				routineId: onboardingRoutineId,
				userID: user.id,
				source: 'onboarding'
			}
			: null;

		if (customData || checkoutRedirectUrl) {
			payload.data.attributes = {};
		}
		if (customData) {
			payload.data.attributes.checkout_data = {
				custom: customData
			};
		}
		if (checkoutRedirectUrl) {
			payload.data.attributes.product_options = {
				redirect_url: checkoutRedirectUrl
			};
		}

		return payload;
	}, [checkoutRedirectUrl, onboardingRoutineId, user?.id]);

	useEffect(() => {
		if (!user) {
			const current = `${location.pathname}${location.search || ''}`;
			const redirectTo = encodeURIComponent(current);
			navigate(`/login?redirectTo=${redirectTo}`, { replace: true });
		}
	}, [user, navigate, location.pathname, location.search]);

	if (!user) {
		return (
			<div className={style.UpgradePage}>
				<div className={style.UpgradePageText}>
					<span className={style.UpgradePageHeading}>Redirecting to login…</span>
				</div>
			</div>
		);
	}

	return (
		<div className={style.UpgradePage}>
			<SEO title="Plans" description="Upgrade to unlock more AI generations, premium features, and priority support on Calcium." path="/upgrade">
				<script src="https://app.lemonsqueezy.com/js/lemon.js" defer></script>
			</SEO>

			<>
					<div className={style.UpgradePageText}>
							<span className={style.UpgradePageHeading}>
								{onboardingSource ? 'Unlock Your 30-Minute Plan' : 'Choose your plan'}
							</span>

							<span className={style.UpgradePageTextContent}>
								<p>{onboardingSource ? 'Your structured week is ready. Choose a plan to unlock workouts, meals, and weekly adaptation.' : ''}</p>
							</span>
						</div>

						<div className={style.UpgradePlansWrapper}>
							<UpgradeCard
								planName='Monthly'
								text='Flexible access'
								amountText='$14/month'
								planType='calcium-monthly'
								lemonSqueezyData={buildCheckoutPayload(process.env.REACT_APP_LS_VARIANT_MONTHLY || '449803')}
							/>

							<UpgradeCard
								planName='6-Month Plan'
								text='Best for building consistency'
								amountText='$69/6 months'
								planType='calcium-six-month'
								recommended
								lemonSqueezyData={buildCheckoutPayload(process.env.REACT_APP_LS_VARIANT_SIX_MONTH || '449804')}
							/>

							<UpgradeCard
								planName='Annual'
								text='Best value'
								amountText='$99/year'
								planType='calcium-annually'
								lemonSqueezyData={buildCheckoutPayload(process.env.REACT_APP_LS_VARIANT_YEARLY || '449802')}
							/>
						</div>

						<div className={style.ExploreButtonWrapper}>
							<SecondaryButton
								text="I'm just exploring"
								click={() => navigate('/')}
								width="280px"
							/>
						</div>
			</>

		</div>
	)
}

export default UpgradePage;
